import React, { type ReactNode } from 'react';
import type ApolloClient from 'apollo-client';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import AddonIcon from '@atlaskit/icon/core/app';
import Image from '@atlaskit/image';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { N60 } from '@atlaskit/theme/colors';
import { ForgeModalDeprecationMessage } from './ForgeModalDeprecationMessage';
import { type ModalExtension } from './util';
import { getADSModalWidth } from '../utils';
import { type Extension } from '../web-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { ConfluenceExtension } from '@atlassian/forge-ui-types';

const styles = cssMap({
	modalWrapper: {
		paddingBottom: token('space.300'),
	},
});

const getExtensionIcon = (extension?: Extension): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};

interface ForgeExtensionPointModalProps {
	children: ReactNode;
	client?: ApolloClient<any>;
	extension?: Extension;
	modalExtension: ModalExtension;
	shouldShowFCFModalDeprecationMessage?: boolean;
}

const ModalWrapper = (props: {
	children: ReactNode;
	closeModalExtension: () => void;
	extensionType?: string;
	shouldCloseOnEscapePress?: boolean;
	shouldCloseOnOverlayClick?: boolean;
	width?: string;
}) => {
	const { closeModalExtension, extensionType, width, shouldCloseOnOverlayClick, shouldCloseOnEscapePress } = props;

	const isFullscreenAllowed = 
		(extensionType === ConfluenceExtension.MACRO && fg('forge_enable_fullscreen_viewport_for_custom_config')) ||
		(extensionType === ConfluenceExtension.BYLINE && fg('platform_add_modal_support_to_bylineitem_extension'));

	if (width === 'fullscreen' && isFullscreenAllowed) {
		return (
			<FullScreenModalDialog
				shouldCloseOnEscapePress={shouldCloseOnEscapePress}
				onClose={closeModalExtension}
			>
				{props.children}
			</FullScreenModalDialog>
		);
	} else {
		return (
			<Modal
				shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
				shouldCloseOnEscapePress={shouldCloseOnEscapePress}
				onClose={closeModalExtension}
				width={width}
			>
				{props.children}
			</Modal>
		);
	}
};

export const ForgeExtensionPointModal = ({
	children,
	modalExtension,
	shouldShowFCFModalDeprecationMessage,
	client,
	extension,
}: ForgeExtensionPointModalProps): React.JSX.Element => {
	const {
		closeModalExtension,
		title,
		modalWidth,
		shouldCloseOnOverlayClick,
		shouldCloseOnEscapePress,
	} = modalExtension;

	const ADSModalWidth = modalWidth ? getADSModalWidth(modalWidth) : undefined;
	const isMacroExtension = extension?.type === ConfluenceExtension.MACRO;
	const extensionIcon = isMacroExtension ? getExtensionIcon(extension) : undefined;

	return (
		<ModalWrapper
			shouldCloseOnEscapePress={shouldCloseOnEscapePress}
			closeModalExtension={closeModalExtension}
			extensionType={extension?.type}
			width={ADSModalWidth}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
		>
			<ModalHeader>
				{isMacroExtension && modalWidth === 'fullscreen' && fg('forge_enable_fullscreen_viewport_for_custom_config') ? (
					<Inline space="space.100" alignBlock="center">
					{extensionIcon ? (
						<Image src={extensionIcon} alt="Macro custom config icon" width={token('space.300')} height={token('space.300')} />
					) : (
							<AddonIcon label="" color={token('color.icon.accent.gray', N60)} />
						)}
						<ModalTitle>{title}</ModalTitle>
					</Inline>
				) : (
					<ModalTitle>{title}</ModalTitle>
				)}
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label="Close Modal"
					onClick={closeModalExtension}
				/>
			</ModalHeader>
			<ModalBody>
				{shouldShowFCFModalDeprecationMessage && extension && client ? (
					<Stack space="space.200">
						<ForgeModalDeprecationMessage client={client} extension={extension} />
						<Box xcss={styles.modalWrapper}>{children}</Box>
					</Stack>
				) : (
					<Box xcss={styles.modalWrapper}>{children}</Box>
				)}
			</ModalBody>
		</ModalWrapper>
	);
};
